<template>
  <div class="mb-3">
    <div role="button"
         tabindex="0"
         @click="toggleDialog"
         class="w-full p-2 cursor-pointer flex items-center hover:underline hover:bg-gray-200 rounded">
      <SearchIcon
        class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300 group-focus:text-gray-300 transition ease-in-out duration-150"/>
      <span class="">{{$t('generic.quickStart')}}</span>
    </div>

    <modal :show.sync="showDialog"
           append-to-body
           :show-close="false"
           class="search-dialog"
    >
      <div class="search-dialog-input__wrapper">
        <SearchIcon v-if="!loading" class="w-6 h-6 mr-2 text-gray-400"></SearchIcon>
        <span v-if="loading" class="flex items-center w-6 mr-2">
              <i class="el-icon-loading text-xl text-grey-dark"></i>
        </span>
        <input class="search-dialog__input"
               :placeholder="$t('generic.applicationSearch')"
               type="text"
               ref="searchInput"
               v-model="searchQuery"
               @keydown.down.prevent="focusOnOption(0)"
        >
      </div>
      <div v-if="searchResults.length"
           class="search-results">
        <div v-for="(item, index) in searchResults"
             :key="item.name"
             tabindex="0"
             :ref="`result-${index}`"
             class="search-result__item"
             :class="{'mb-3': index === searchResults.length - 1}"
             @keydown.down.prevent="onKeyArrowDown"
             @keydown.up.prevent="onKeyArrowUp"
             @click="navigateToItem(item)"
             @keydown.enter="navigateToItem(item)"
        >
          <div v-html="highlightResult(item.title)"></div>
          <div class="text-sm text-gray-700 mt-1" v-html="highlightResult(item.description)"></div>
        </div>


      </div>
    </modal>
  </div>
</template>
<script>
  import { SearchIcon } from 'vue-feather-icons'
  import get from 'lodash/get'

  export default {
    components: {
      SearchIcon,
    },
    data () {
      return {
        showDialog: false,
        loading: false,
        searchQuery: '',
        searchResults: [],
        focusedOptionIndex: 0,
      }
    },
    computed: {
      resultsCount () {
        return this.searchResults.length
      },
      routes () {
        let result = []
        if (this.$router.options.routes) {
          this.$router.options.routes.forEach(route => {
            result.push(...this.getNestedRoutes(route))
          })
        }
        return result.filter(r => r.title)
      }

    },
    watch: {
      searchQuery (query) {
        this.onSearchChange(query)
      },
    },
    methods: {
      highlightResult (name) {
        name = this.$t(name) || ''
        const characters = get(this.searchQuery, 'length', 0)
        let query = (this.searchQuery || '').toLowerCase()
        const characterStart = name.toLowerCase().indexOf(query)
        if (characterStart === -1) {
          return name
        }

        const searchedString = name.substr(characterStart, characters)
        return name.substr(0, characterStart) +
          `<span class="highlight">${searchedString}</span>` +
          name.substr(characterStart + characters, name.length)
      },
      getNestedRoutes (route) {
        let results = []
        if (!route.children) {
          return results
        }
        route.children.forEach(route => {
          results.push(route)
          results.push(...this.getNestedRoutes(route))
        })
        return results
      },
      routeSearchFilter (query, route) {
        if (!query) {
          return true
        }
        let {title, description} = route
        title = this.$t(title) || ''
        description = this.$t(description) || ''
        return title.toLowerCase().includes(query) || description.toLowerCase().includes(query)
      },
      async onSearchChange (query) {
        let searchResults = []
        if (!query) {
          this.searchResults = searchResults
        }
        this.$emit('change', query)
        try {
          this.loading = true

          if (!query) {
            searchResults = this.routes.sort((a, b) => {
              return this.$t(a.title).toLowerCase().localeCompare(this.$t(b.title).toLowerCase())
            })
          } else {
            searchResults = this.routes.filter(route => this.routeSearchFilter(query, route))
          }
          this.searchResults = searchResults
        } catch (e) {
          this.searchResults = searchResults
        } finally {
          this.loading = false
        }
      },
      toggleDialog () {
        this.showDialog = !this.showDialog
        this.$nextTick(() => {
          this.$refs.searchInput.focus()
        })
      },
      onKeyArrowDown () {
        if (this.focusedOptionIndex < this.resultsCount - 1) {
          this.focusedOptionIndex++
        } else {
          this.focusedOptionIndex = 0
        }
        this.focusOnOption(this.focusedOptionIndex)
      },
      onKeyArrowUp () {
        if (this.focusedOptionIndex > 0) {
          this.focusedOptionIndex--
        } else {
          this.focusedOptionIndex = this.resultsCount - 1
        }
        this.focusOnOption(this.focusedOptionIndex)
      },
      focusOnOption (index) {
        if (!this.resultsCount) {
          return
        }
        this.$nextTick(() => {
          const ref = get(this.$refs[`result-${index}`], '[0]')
          if (!ref) {
            return
          }
          ref.focus()
          if (this.focusedOptionIndex !== index) {
            this.focusedOptionIndex = index
          }
        })
      },
      navigateToItem (item) {
        this.$router.push(item)
        this.showDialog = false
        this.searchQuery = ''
      }
    },
  }
</script>
<style lang="scss">
  .search-dialog {
    .modal-dialog .modal-header, {
      display: none !important;
    }

    .modal-header,
    .modal-body {
      @apply p-0;
    }

    .search-dialog-input__wrapper {
      height: 50px;
      @apply px-4 flex items-center;
    }

    .search-dialog__input {
      height: 28px;
      width: calc(100% - 55px);
      @apply border-none text-lg;
      &:focus,
      &:hover,
      &:active {
        @apply border-none outline-none;
      }

      &::placeholder {
        @apply text-gray-700;
      }
    }

    .el-dialog__headerbtn {
      top: 16px;
      right: 16px;

      .close-icon {
        @apply text-gray-600;
      }
    }

    .search-results {
      max-height: calc(100vh - 10vh);
      @apply flex flex-col overflow-y-auto;
      @screen md {
        max-height: 400px;
      }
    }

    .search-result__item {
      @apply py-3 px-4 flex flex-col justify-center cursor-pointer text-base font-medium;
      &.selected,
      &:focus,
      &:hover {
        @apply bg-gray-100 outline-none;
      }

      .highlight {
        @apply text-primary;
      }
    }
  }
</style>
